/**
 * A reflection on its own page, laid out like a lab page: a cover banner (the reflection's image, or the
 * author's page colour) with the title and headline; below, the text (or recording) and, on the right, a
 * card with the author, dates, reading time, the profile item it is about, the tags and, for the author,
 * edit and delete, then more reflections by the same person. Comments follow the text, in its column.
 * Routes: /reflections/:objectId/ and, for reflections marked public, /p/reflection/:objectId/ (no account,
 * Otable logo on the cover, no comments).
 * Backend: GET /reflections/<id>/article/ | /public/reflection/<id>/, GET /reflections/timeline/?author=<id>.
 */
import React, { useState } from 'react';
import useSWR from 'swr';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Avatar, Box, Button, Chip, CircularProgress, Container, Divider, Stack, Typography } from '@mui/material';
import {
  CalendarToday as DateIcon, Delete as DeleteIcon, Edit as EditIcon, Groups as ConnectionsIcon, Lock as LockIcon,
  Public as PublicIcon, Schedule as TimeIcon, Update as UpdatedIcon,
} from '@mui/icons-material';
import { axiosService, fetcher } from 'shared/helpers/axios';
import { useUserContext } from 'shared/contexts/UserContext';
import { useSnackbar } from 'shared/contexts/SnackbarContext';
import LabMarkdown from 'components/collab/lab/LabMarkdown';
import { Comments } from 'components/common/Comments';
import ReflectionEditor from './ReflectionEditor';
import ReportButton from 'components/common/ReportButton';
import { DISCLAIMER, GuidelinesLink } from './reflectionGuidelines';
import { CoverHero, PageBackground, PublicFooter, Section, pageColor } from './ReflectionsShell';

const dayOf = (iso) => new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' });
const AUDIENCE = {
  connections: [<ConnectionsIcon key="i" sx={{ fontSize: 18 }} />, 'My connections'],
  otable: [<LockIcon key="i" sx={{ fontSize: 18 }} />, 'Everyone on Otable'],
  public: [<PublicIcon key="i" sx={{ fontSize: 18 }} />, 'Anyone, on my public page'],
};

function Fact({ icon, children }) {
  return (
    <Box display="flex" alignItems="center" gap={1} sx={{ color: 'text.secondary' }}>
      {icon}<Typography variant="body2" component="span" sx={{ color: 'text.primary' }}>{children}</Typography>
    </Box>
  );
}

export default function ReflectionArticlePage({ publicMode = false }) {
  const { objectId } = useParams();
  const navigate = useNavigate();
  const userCtx = useUserContext() || {};
  const user = publicMode ? null : userCtx.user;
  const { showSnackbar } = useSnackbar() || {};
  const key = objectId ? (publicMode ? `/public/reflection/${objectId}/` : `/reflections/${objectId}/article/`) : null;
  const { data: r, error, mutate } = useSWR(key, fetcher);
  // more by the same person: their timeline; on a public article, their public page's reflections
  const moreKey = r?.author?.id && (publicMode ? (r.page_is_public ? `/public/reflections/${r.author.id}/` : null)
    : `/reflections/timeline/?author=${r.author.id}`);
  const { data: moreData } = useSWR(moreKey || null, fetcher);
  const more = publicMode ? moreData?.timeline : moreData;
  const [editing, setEditing] = useState(false);
  const own = r && user && String(user.id) === String(r.author.id);

  const remove = async () => {
    if (!window.confirm('Delete this reflection? Its comments go with it. This cannot be undone.')) return;
    try {
      await axiosService.delete(`/reflections/${r.id}/`);
      showSnackbar?.('Reflection deleted', 'success');
      navigate(`/profile/${r.author.id}/reflections/`);
    } catch {
      showSnackbar?.('The reflection could not be deleted', 'error');
    }
  };

  if (error) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Alert severity="info">
          {publicMode ? 'This reflection is not public.'
            : error.response?.status === 403 ? 'This reflection is shared with the author’s connections only. Ask to connect with them to read it.'
              : 'This reflection could not be loaded.'}
        </Alert>
      </Container>
    );
  }
  if (!r) return <Box display="grid" sx={{ placeItems: 'center', py: 10 }}><CircularProgress /></Box>;

  const look = { cover_url: r.cover_url, background_color: r.page_color };
  const color = pageColor(look);
  const others = (more?.entries || []).filter((e) => e.id !== r.id && e.status === 'published').slice(0, 4);
  const pageLink = publicMode ? (r.page_is_public ? `/p/reflections/${r.author.id}/` : null) : `/profile/${r.author.id}/reflections/`;

  return (
    <PageBackground page={look} publicMode={publicMode}>
      <CoverHero cover={look} publicMode={publicMode} minHeight={r.cover_url ? 340 : 240}>
        {r.status === 'draft' && <Chip label="Draft · only you can see it" size="small" sx={{ mb: 1, bgcolor: 'rgba(0,0,0,0.35)', color: '#fff' }} />}
        <Typography component="h1" sx={{ fontWeight: 800, fontSize: { xs: '1.9rem', md: '2.7rem' }, lineHeight: 1.15, maxWidth: 900 }}>{r.title}</Typography>
        {r.subtitle && <Typography sx={{ fontSize: { xs: 17, md: 20 }, mt: 1, opacity: 0.95, maxWidth: 900 }}>{r.subtitle}</Typography>}
        <Box display="flex" alignItems="center" gap={1.25} sx={{ mt: 2 }}>
          <Avatar src={r.author.avatar_url || undefined} sx={{ width: 36, height: 36, border: '2px solid #fff' }}>{(r.author.name || '?')[0]}</Avatar>
          <Typography sx={{ fontSize: 15, fontWeight: 600, cursor: pageLink ? 'pointer' : 'default' }}
            onClick={() => pageLink && navigate(pageLink)}>{r.author.name}</Typography>
          <Typography sx={{ fontSize: 14, opacity: 0.9 }}>· {dayOf(r.date)} · {r.reading_minutes} min read</Typography>
        </Box>
      </CoverHero>

      <Container maxWidth="lg" sx={{ mt: 3 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, gap: 2, alignItems: 'start' }}>
          {/* the text, and the comments under it */}
          <Box minWidth={0}>
          <Section color={color} sx={{ p: { xs: 2.5, sm: 4 } }}>
            {r.media_type === 'audio' && r.audio_url && <Box component="audio" controls src={r.audio_url} sx={{ width: '100%', mb: 2 }} />}
            {r.media_type === 'video' && r.video_url && <Box component="video" controls src={r.video_url} sx={{ width: '100%', borderRadius: 1, mb: 2 }} />}
            <Box sx={{ '& p': { fontSize: '16.5px', lineHeight: 1.8 } }}><LabMarkdown>{r.body || ''}</LabMarkdown></Box>
            {/* the disclaimer, the guidelines, and a way to report the reflection */}
            <Divider sx={{ mt: 3, mb: 1.5 }} />
            <Box display="flex" alignItems="flex-start" gap={2} flexWrap="wrap">
              <Typography variant="caption" color="text.secondary" sx={{ flex: 1, minWidth: 240 }}>
                {DISCLAIMER} <GuidelinesLink sx={{ fontSize: 'inherit', verticalAlign: 'baseline' }}>Reflection guidelines</GuidelinesLink>
              </Typography>
              {!own && !publicMode && (
                <ReportButton targetType="reflection" targetId={r.id} targetLabel={r.title} variant="button" size="small" />
              )}
              {publicMode && (
                <Button size="small" component="a" href="/login/" sx={{ color: 'text.secondary' }}>Sign in to report</Button>
              )}
            </Box>
          </Section>
          {!publicMode && r.status === 'published' && (
            <Section title="Comments" color={color}>
              <Comments targetId={r.id} targetType="reflection" currentUser={user} />
            </Section>
          )}
          </Box>

          {/* about this reflection */}
          <Box sx={{ position: { md: 'sticky' }, top: { md: 80 } }}>
            <Section title="About this reflection" color={color}>
              <Stack spacing={1.25}>
                <Fact icon={<DateIcon sx={{ fontSize: 18 }} />}>{r.status === 'draft' ? 'Not published yet' : `Published ${dayOf(r.date)}`}</Fact>
                {r.updated && new Date(r.updated) - new Date(r.date) > 864e5 && <Fact icon={<UpdatedIcon sx={{ fontSize: 18 }} />}>Updated {dayOf(r.updated)}</Fact>}
                <Fact icon={<TimeIcon sx={{ fontSize: 18 }} />}>{r.reading_minutes} min read</Fact>
                {own && AUDIENCE[r.visibility] && <Fact icon={AUDIENCE[r.visibility][0]}>{AUDIENCE[r.visibility][1]}</Fact>}
              </Stack>
              {r.item && (
                <>
                  <Divider sx={{ my: 1.75 }} />
                  <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}>On {r.item.kind.toLowerCase()}</Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>{r.item.label}</Typography>
                  {!publicMode && (
                    <Button size="small" sx={{ px: 0, mt: 0.25 }}
                      onClick={() => navigate(`${pageLink}?item=${r.item.content_type.split('.').pop()}:${r.item.object_id}`)}>
                      All reflections on this {r.item.kind.toLowerCase()}
                    </Button>
                  )}
                </>
              )}
              {r.tags.length > 0 && (
                <>
                  <Divider sx={{ my: 1.75 }} />
                  <Box display="flex" gap={0.75} flexWrap="wrap">
                    {/* a tag opens the author's reflections with that tag */}
                    {r.tags.map((t) => (
                      <Chip key={t} label={t} size="small" variant="outlined" clickable={Boolean(pageLink)}
                        onClick={pageLink ? () => navigate(`${pageLink}?tag=${encodeURIComponent(t)}`) : undefined} />
                    ))}
                  </Box>
                </>
              )}
              {own && (
                <>
                  <Divider sx={{ my: 1.75 }} />
                  <Box display="flex" gap={1}>
                    <Button size="small" variant="outlined" startIcon={<EditIcon />} onClick={() => setEditing(true)}>Edit</Button>
                    <Button size="small" color="error" startIcon={<DeleteIcon />} onClick={remove}>Delete</Button>
                  </Box>
                </>
              )}
            </Section>

            {others.length > 0 && (
              <Section title={`More from ${r.author.name.split(' ')[0]}`} color={color}>
                <Stack spacing={1.25} divider={<Divider flexItem />}>
                  {others.map((e) => (
                    <Box key={e.id} sx={{ cursor: 'pointer', '&:hover .t': { textDecoration: 'underline' } }} onClick={() => navigate(publicMode ? `/p/reflection/${e.id}/` : `/reflections/${e.id}/`)}>
                      <Typography className="t" variant="body2" sx={{ fontWeight: 600 }}>{e.title}</Typography>
                      <Typography variant="caption" color="text.secondary">{dayOf(e.date)} · {e.reading_minutes} min read</Typography>
                    </Box>
                  ))}
                </Stack>
              </Section>
            )}
            {pageLink && (
              <Button fullWidth variant="outlined" onClick={() => navigate(pageLink)} sx={{ borderColor: color, color }}>
                All reflections by {r.author.name.split(' ')[0]}
              </Button>
            )}
          </Box>
        </Box>

        {publicMode && <PublicFooter />}
      </Container>

      {own && (
        <ReflectionEditor open={editing} entry={r} tagSuggestions={(more?.tags || []).map((t) => t.tag)}
          onClose={() => setEditing(false)} onSaved={() => { setEditing(false); mutate(); }} />
      )}
    </PageBackground>
  );
}
